import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Globe2,
  Link2,
  Loader2,
  Package,
  Plane,
  AlertCircle,
  MapPin,
  Crown,
} from "lucide-react";
import { useDestination } from "../App";
import { fetchFedExRates } from "../services/shippingApi";
import { CONCIERGE_TIERS } from "../config/mansionSeo";
import {
  getOriginMarketplace,
  listAllOrigins,
  listPopularOrigins,
  resolveOriginCode,
} from "../config/globalCarrierLanes";
import { getDestinationConfig, listShopDestinations } from "../config/caribbeanDestinations";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const DEFAULT_WEIGHT = "2";

function resolveDestCode(code) {
  const upper = (code || "BB").toUpperCase();
  return upper === "UK" ? "GB" : upper;
}

function formatLaneLabel(originCode, destCode, importLane) {
  if (importLane?.label) return importLane.label;
  if (importLane?.uses_barbados_hub) return "Via Barbados hub clearance";
  if (originCode && destCode && originCode !== destCode) {
    return "Direct FedEx + DHL carrier lane";
  }
  return "International import lane";
}

export default function CrossCountryShippingSection({ showTierSelector = true }) {
  const navigate = useNavigate();
  const { destination, setDestination } = useDestination();
  const [originCode, setOriginCode] = useState("JP");
  const [destCode, setDestCode] = useState(resolveDestCode(destination));
  const [weight, setWeight] = useState(DEFAULT_WEIGHT);
  const [conciergeTier, setConciergeTier] = useState("premium_import");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [quote, setQuote] = useState(null);

  const origins = useMemo(() => listPopularOrigins(), []);
  const allOrigins = useMemo(() => listAllOrigins(), []);
  const destinations = useMemo(() => listShopDestinations(), []);

  useEffect(() => {
    setDestCode(resolveDestCode(destination));
  }, [destination]);

  const originMeta = allOrigins.find((o) => o.code === originCode) || origins[0];
  const destMeta =
    getDestinationConfig(destCode) ||
    destinations.find((d) => resolveDestCode(d.code) === destCode);

  const handleQuote = async () => {
    if (!originCode || !destCode || !weight) return;
    setLoading(true);
    setError(null);
    setQuote(null);

    const marketplace = getOriginMarketplace(originCode);

    try {
      const result = await fetchFedExRates({
        destinationCountry: resolveDestCode(destCode) === "GB" ? "GB" : destCode,
        originCountry: resolveOriginCode(originCode),
        sourceUrl: marketplace.sampleUrl,
        marketplace: marketplace.marketplace,
        conciergeTier,
        weightKg: parseFloat(weight),
      });
      setQuote(result);
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        "Could not fetch cross-country rates";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const displayOrigin = quote?.import_lane?.origin_country || quote?.origin || originCode;
  const displayDest =
    quote?.customer_destination || quote?.import_lane?.destination_country || quote?.destination || destCode;
  const usesHub = quote?.uses_barbados_hub ?? quote?.import_lane?.uses_barbados_hub;

  return (
    <section
      id="cross-country-shipping"
      className="py-24 md:py-32 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 text-white relative overflow-hidden scroll-mt-20"
      data-testid="cross-country-shipping"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 bg-orange-500/15 border border-orange-500/30 text-orange-300 text-xs font-semibold px-4 py-2 rounded-full mb-6">
            <Globe2 className="h-4 w-4" />
            Cross-Country Shipping
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-4">
            Import from anywhere.
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-amber-400">
              Delivered anywhere FedEx &amp; DHL go.
            </span>
          </h2>
          <p className="text-slate-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Shop Japan, UK, Australia, Canada, the US, China, and more — premium global lanes with
            supplier pickup and door-to-door delivery in one fully cleared price.
          </p>
          <p className="mt-4">
            <button
              type="button"
              onClick={() => navigate("/global-shipping")}
              className="text-sm text-orange-400 hover:text-orange-300 font-semibold underline underline-offset-4"
              data-testid="cross-country-full-page-link"
            >
              Open full Global Shipping page →
            </button>
          </p>
        </motion.div>

        {/* Route visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-10"
        >
          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-6 py-4 min-w-[200px]">
            <span className="text-3xl">{originMeta?.flag}</span>
            <div className="text-left">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Ship from</p>
              <p className="font-bold text-white">{originMeta?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-orange-400">
            <Plane className="h-5 w-5 hidden sm:block" />
            <ArrowRight className="h-6 w-6" />
            <Package className="h-5 w-5 hidden sm:block" />
          </div>

          <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-6 py-4 min-w-[200px]">
            <span className="text-3xl">{destMeta?.flag || "🌍"}</span>
            <div className="text-left">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Deliver to</p>
              <p className="font-bold text-white">{destMeta?.name || destCode}</p>
            </div>
          </div>
        </motion.div>

        {/* Quote panel */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl"
        >
          <p className="text-sm text-slate-400 mb-6 text-center">
            Select an origin marketplace country and your destination to preview FedEx + DHL freight.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
                Origin country (supplier pickup)
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {origins.map((o) => (
                  <button
                    key={o.code}
                    type="button"
                    onClick={() => setOriginCode(o.code)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      originCode === o.code
                        ? "bg-orange-500 text-white shadow-lg shadow-orange-500/30"
                        : "bg-white/10 text-slate-300 hover:bg-white/15"
                    }`}
                    data-testid={`origin-${o.code}`}
                  >
                    <span>{o.flag}</span>
                    <span>{o.code}</span>
                  </button>
                ))}
              </div>
              <Select value={originCode} onValueChange={setOriginCode}>
                <SelectTrigger className="bg-slate-900/80 border-white/15 text-white" data-testid="origin-select">
                  <SelectValue placeholder="All origin markets" />
                </SelectTrigger>
                <SelectContent>
                  {allOrigins.map((o) => (
                    <SelectItem key={o.code} value={o.code}>
                      <span className="flex items-center gap-2">
                        <span>{o.flag}</span>
                        <span>{o.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
                Destination
              </label>
              <Select
                value={destCode}
                onValueChange={(v) => {
                  setDestCode(v);
                  setDestination(v);
                }}
              >
                <SelectTrigger className="bg-slate-900/80 border-white/15 text-white" data-testid="dest-select">
                  <SelectValue placeholder="Your country" />
                </SelectTrigger>
                <SelectContent>
                  {destinations.map((d) => (
                    <SelectItem key={d.code} value={d.code}>
                      <span className="flex items-center gap-2">
                        <span>{d.flag}</span>
                        <span>{d.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-2 uppercase tracking-wider">
                Weight (kg)
              </label>
              <Input
                type="number"
                min="0.5"
                step="0.5"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="bg-slate-900/80 border-white/15 text-white"
                data-testid="cross-country-weight"
              />
            </div>
          </div>

          {showTierSelector && (
            <div className="mb-6">
              <label className="block text-xs font-medium text-slate-400 mb-3 uppercase tracking-wider text-center">
                Service level
              </label>
              <div className="grid sm:grid-cols-3 gap-3">
                {CONCIERGE_TIERS.map((tier) => (
                  <button
                    key={tier.id}
                    type="button"
                    onClick={() => setConciergeTier(tier.id)}
                    className={`text-left rounded-xl border p-4 transition-all ${
                      conciergeTier === tier.id
                        ? "border-orange-500 bg-orange-500/15 shadow-lg shadow-orange-500/20"
                        : "border-white/10 bg-white/5 hover:border-white/20"
                    }`}
                    data-testid={`tier-${tier.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-bold text-white text-sm">{tier.label}</span>
                      <span className="text-[10px] uppercase tracking-wider bg-white/10 text-orange-300 px-2 py-0.5 rounded-full">
                        {tier.badge}
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 leading-relaxed">{tier.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleQuote}
            disabled={loading || !originCode || !destCode || !weight}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white font-semibold py-6 text-base rounded-xl"
            data-testid="cross-country-quote-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Fetching live carrier rates…
              </>
            ) : (
              <>
                <MapPin className="w-5 h-5 mr-2" />
                Get Cross-Country Shipping Quote
              </>
            )}
          </Button>

          {error && (
            <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-200">{error}</p>
            </div>
          )}

          {quote && (
            <div className="mt-6 border-t border-white/10 pt-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Import lane</p>
                  <p className="font-semibold text-white">
                    {displayOrigin} → {displayDest}
                    {usesHub && (
                      <span className="ml-2 text-xs font-normal text-amber-300">(Barbados hub)</span>
                    )}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {formatLaneLabel(displayOrigin, displayDest, quote.import_lane)}
                    {quote.provider ? ` · ${quote.provider}` : ""}
                  </p>
                  {conciergeTier === "premium_import" && (
                    <p className="inline-flex items-center gap-1 text-xs text-amber-300 mt-2">
                      <Crown className="h-3.5 w-3.5" />
                      High-ticket white-glove import lane
                    </p>
                  )}
                </div>
                {quote.sandbox && (
                  <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-1 rounded-full">
                    Sandbox rates
                  </span>
                )}
              </div>

              {quote.rates?.length > 0 ? (
                <ul className="space-y-2">
                  {quote.rates.slice(0, 4).map((rate) => (
                    <li
                      key={`${rate.carrier}-${rate.service}`}
                      className="flex items-center justify-between gap-4 bg-white/5 rounded-xl px-4 py-3 border border-white/5"
                      data-testid={`cross-rate-${rate.service}`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-white truncate">{rate.name}</p>
                        <p className="text-xs text-slate-500">
                          {rate.carrier}
                          {rate.estimatedDelivery && rate.estimatedDelivery !== "N/A"
                            ? ` · ${rate.estimatedDelivery} days`
                            : rate.transit_days && rate.transit_days !== "N/A"
                              ? ` · ${rate.transit_days} days`
                              : ""}
                        </p>
                      </div>
                      <p className="text-lg font-bold text-orange-400 shrink-0">
                        ${Number(
                          rate.totalLandedCost ?? rate.total ?? rate.combined_total ?? 0
                        ).toFixed(2)}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No rates returned for this lane. Try another origin or destination.
                </p>
              )}

              <p className="text-xs text-slate-500 mt-4 text-center">
                Final price at checkout is all-inclusive — freight, customs, and clearance included.
              </p>
            </div>
          )}
        </motion.div>

        {/* Popular routes grid */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
          {[
            { from: "JP", to: "BB", label: "Japan → Barbados" },
            { from: "GB", to: "TT", label: "UK → Trinidad" },
            { from: "US", to: "JM", label: "US → Jamaica" },
            { from: "AU", to: "LC", label: "Australia → St. Lucia" },
          ].map((route) => {
            const fromO = allOrigins.find((o) => o.code === route.from);
            const toD = destinations.find((d) => d.code === route.to);
            return (
              <button
                key={route.label}
                type="button"
                onClick={() => {
                  setOriginCode(route.from);
                  setDestCode(route.to);
                  setDestination(route.to);
                }}
                className="text-left p-4 rounded-xl bg-white/5 border border-white/10 hover:border-orange-500/40 hover:bg-white/10 transition-all"
                data-testid={`route-${route.from}-${route.to}`}
              >
                <p className="text-lg mb-1">
                  {fromO?.flag} <ArrowRight className="inline h-3 w-3 text-orange-400" /> {toD?.flag}
                </p>
                <p className="text-xs text-slate-400">{route.label}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Button
            onClick={() => navigate("/submit")}
            className="bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 hover:from-orange-400 hover:via-amber-400 hover:to-orange-500 text-white px-8 py-6 text-base font-semibold shadow-xl shadow-orange-500/30"
            data-testid="cross-country-submit-link"
          >
            <Link2 className="mr-2 h-5 w-5" />
            Have a product link? Submit it for your landed price
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}
